import React from "react";
import Current from "./Current";
import Form from "./Form";
import Users from "./Users";
import Revenues from "./Revenues";
import Invoices from "./Invoices";
import ActivityCrm from "./ActivityCrm";
import StripeActivity from "./StripeActivity";
import DemoActivity from "../UserActivity/DemoActivity";
import CampaignInbox from "./CampaignInbox";
import axios from "axios";
import "./sales.css";

// Stripe Revenue components
import SummaryCards from "../StripeRevenue/SummaryCards";
import MRRChart from "../StripeRevenue/MRRChart";
import SubscriptionsTab from "../StripeRevenue/SubscriptionsTab";
import InvoicesTab from "../StripeRevenue/InvoicesTab";
import TransactionsTab from "../StripeRevenue/TransactionsTab";
import ChurnedTab from "../StripeRevenue/ChurnedTab";
import DateFilter from "../StripeRevenue/DateFilter";

// Icons — existing
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InsightsIcon from "@mui/icons-material/Insights";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import VideocamIcon from "@mui/icons-material/Videocam";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

// Icons — Stripe Revenue
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";

const EXISTING_TABS = [
  { index: 0, label: "Users",          icon: PeopleAltIcon },
  { index: 1, label: "Current Status", icon: ViewKanbanIcon },
  { index: 2, label: "Add Data",       icon: AddCircleOutlineIcon },
  { index: 3, label: "Revenues",       icon: TrendingUpIcon },
  { index: 4, label: "Invoices",       icon: ReceiptLongIcon },
  { index: 5, label: "Activity CRM",   icon: InsightsIcon },
  { index: 6, label: "Stripe Activity",icon: CreditCardIcon },
  { index: 12, label: "Demo Meetings", icon: VideocamIcon },
  { index: 13, label: "My Inbox",icon: MailOutlineIcon },
];

const STRIPE_TABS = [
  { index: 7,  label: "Overview",        icon: DashboardOutlinedIcon },
  { index: 8,  label: "Subscriptions",   icon: AutorenewIcon },
  { index: 9,  label: "Stripe Invoices", icon: ReceiptIcon },
  { index: 10, label: "Transactions",    icon: SwapHorizIcon },
  { index: 11, label: "Churned",         icon: PersonRemoveOutlinedIcon },
];

const ALL_TABS = [...EXISTING_TABS, ...STRIPE_TABS];

function StripeOverview() {
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [appliedRange, setAppliedRange] = React.useState({ startDate: "", endDate: "" });

  const handleApply = () => setAppliedRange({ startDate, endDate });
  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setAppliedRange({ startDate: "", endDate: "" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <DateFilter
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          onApply={handleApply}
          onClear={handleClear}
        />
      </div>
      <SummaryCards startDate={appliedRange.startDate} endDate={appliedRange.endDate} />
      <MRRChart startDate={appliedRange.startDate} endDate={appliedRange.endDate} />
    </div>
  );
}

// The Gmail OAuth callback redirects back to this page with
// ?campaignInboxAuth=... — without this, that lands on the default Users
// tab (index 0) and Campaign Inbox never mounts to handle its own redirect.
function getInitialTab() {
  const params = new URLSearchParams(window.location.search);
  return params.has("campaignInboxAuth") ? 13 : 0;
}

export default function Sales() {
  const [value, setValue] = React.useState(getInitialTab);
  const [leads, setLeads] = React.useState([]);
  const [activityCrm, setActivityCrm] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    axios.get("/api/sale/all-users").then((res) => {
      if (res.data.success) setLeads(res.data.data);
    });
    axios.get("/api/activity/activitycrm").then((res) => {
      if (res.data.success) setActivityCrm(res.data.data);
    });
  }, []);

  const PANEL_MAP = {
    0:  <Users setValue={setValue} value={value} leads={leads} />,
    1:  <Current setValue={setValue} value={value} />,
    2:  <Form setValue={setValue} value={value} leads={leads} />,
    3:  <Revenues setValue={setValue} value={value} />,
    4:  <Invoices setValue={setValue} value={value} />,
    5:  <ActivityCrm activityCrm={activityCrm} setValue={setValue} value={value} leads={activityCrm} />,
    6:  <StripeActivity />,
    7:  <StripeOverview />,
    8:  <SubscriptionsTab />,
    9:  <InvoicesTab />,
    10: <TransactionsTab />,
    11: <ChurnedTab />,
    12: <DemoActivity />,
    13: <CampaignInbox />,
  };

  const currentLabel = ALL_TABS.find((t) => t.index === value)?.label ?? "Sales CRM";

  const renderNavItem = (tab) => {
    const Icon = tab.icon;
    return (
      <button
        key={tab.index}
        className={`sales-nav-item${value === tab.index ? " active" : ""}`}
        onClick={() => setValue(tab.index)}
        title={collapsed ? tab.label : undefined}
      >
        <span className="sales-nav-icon"><Icon /></span>
        <span className="sales-nav-label">{tab.label}</span>
      </button>
    );
  };

  return (
    <div className="sales-layout">
      {/* Sub-sidebar */}
      <aside className={`sales-sidebar${collapsed ? " collapsed" : ""}`}>
        <div className="sales-sidebar-header">
          {!collapsed && <p className="sales-sidebar-title">Sales CRM</p>}
          <button
            className="sidebar-toggle-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>

        <nav className="sales-sidebar-nav">
          {/* Existing tabs */}
          {EXISTING_TABS.map(renderNavItem)}

          {/* ── STRIPE REVENUE divider ── */}
          <div className="sales-sidebar-section">
            <div className="sales-sidebar-section-line" />
            <span className="sales-sidebar-section-label">Stripe Revenue</span>
            <div className="sales-sidebar-section-line" />
          </div>

          {/* New Stripe tabs */}
          {STRIPE_TABS.map(renderNavItem)}
        </nav>
      </aside>

      {/* Content */}
      <main className={`sales-content${collapsed ? " sidebar-collapsed" : ""}`}>
        <div className="sales-content-header">
          <h1 className="sales-content-title">{currentLabel}</h1>
        </div>
        <div className="sales-content-body">
          {PANEL_MAP[value]}
        </div>
      </main>
    </div>
  );
}
